import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { fetchMyAgenda } from "../../../methods/agenda/agendaRequests";

const formatTime = (value) => {
  if (!value) return "--:--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
};

export default function DashboardAgendaPanel() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [items, setItems] = React.useState([]);

  const loadAgenda = React.useCallback(async () => {
    setLoading(true);
    setError("");

    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);

    const response = await fetchMyAgenda({
      start: start.toISOString(),
      end: end.toISOString(),
    });

    if (!response?.success) {
      setItems([]);
      setError(response?.error || "Failed to load agenda.");
      setLoading(false);
      return;
    }

    const agendaItems = (Array.isArray(response.data?.items) ? response.data.items : [])
      .filter((item) => {
        const startsAt = item?.starts_at ? new Date(item.starts_at) : null;
        return startsAt && !Number.isNaN(startsAt.getTime());
      })
      .sort((left, right) => new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime());

    const now = new Date();
    const upcomingItems = agendaItems.filter((item) => new Date(item.starts_at).getTime() >= now.getTime());
    const nextItems = (upcomingItems.length > 0 ? upcomingItems : agendaItems).slice(0, 5);

    setItems(nextItems);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography component="h2" variant="h6">
              Agenda
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Next scheduled items for the next 7 days.
            </Typography>
          </Box>
          <Button component={RouterLink} to="/dashboard/agenda" size="small" variant="outlined">
            Open Agenda
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {!loading && error ? (
          <Alert severity="warning">{error}</Alert>
        ) : null}

        {!loading && !error ? (
          items.length === 0 ? (
            <Typography color="text.secondary">No upcoming agenda items.</Typography>
          ) : (
            <Stack spacing={1.5}>
              {items.map((item, index) => (
                <Box key={item.id || index}>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {item.title || "Agenda Item"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.location || item.subtitle || item.source}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                      {formatTime(item.starts_at)}
                    </Typography>
                  </Stack>
                  {index < items.length - 1 ? <Divider sx={{ mt: 1.5 }} /> : null}
                </Box>
              ))}
            </Stack>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}
